'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { PulsatingBestieAvatar } from '@components/templates/audio-interview/partials/PulsatingBestieAvatar';
import { PhoneXMarkIcon } from '@heroicons/react/24/solid';
import { ElevenLabsConversation } from './ElevenLabsConversation';

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected';

interface VoiceAgentCardProps {
  conversationToken: string | null;
  isLoadingToken: boolean;
  tokenError: string | null;
  locale: Locale;
  onClose: () => void;
}

/**
 * VoiceAgentCard
 *
 * Clean UI for voice conversation:
 * - Shows single unified loading state for token + identity + connection
 * - Timer at top (MM:SS) - only starts when connected
 * - Pulsating Bestie avatar in center - only shows when connected
 * - End button at bottom
 * - Headless conversation engine (no UI)
 */
export default function VoiceAgentCard({
  conversationToken,
  isLoadingToken,
  tokenError,
  locale,
  onClose,
}: VoiceAgentCardProps) {
  const { t } = useTranslation('common');
  const [seconds, setSeconds] = useState(0);
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [isLoadingIdentity, setIsLoadingIdentity] = useState(true);

  // Session timer - only starts when connected
  useEffect(() => {
    if (connectionState !== 'connected') return undefined;

    const interval = setInterval(() => {
      setSeconds((s) => s + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [connectionState]);

  // Format time as MM:SS
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');

  // Determine if we're in any loading state
  const isLoading = isLoadingToken || !conversationToken || isLoadingIdentity || connectionState === 'connecting' || connectionState === 'idle';

  // Show error state
  if (tokenError) {
    return (
      <div className="flex h-[480px] flex-col items-center justify-between px-6 py-8 text-center">
        <div />
        <div className="flex flex-1 flex-col items-center justify-center space-y-4">
          <p className="text-center text-sm text-gray-500">
            {t('global.something.went.wrong')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex size-14 items-center justify-center rounded-full bg-red-500 shadow-lg transition hover:bg-red-600 active:scale-95"
          aria-label="Close"
        >
          <PhoneXMarkIcon className="size-6 text-white" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-[480px] flex-col items-center justify-between px-6 py-8 text-center">
      {isLoading ? (
        <>
          {/* Empty top space for layout consistency */}
          <div className="text-sm tracking-widest text-neutral-500" />

          {/* Loader and status in center */}
          <div className="flex flex-1 flex-col items-center justify-center space-y-4">
            <TailwindSpinner className="size-8" />
            <p className="text-sm text-neutral-500">
              {t('audio-interview.connecting')}
            </p>
          </div>

          {/* ⛔ End Button - available during connecting */}
          <button
            type="button"
            onClick={onClose}
            className="flex size-14 items-center justify-center rounded-full bg-red-500 shadow-lg transition hover:bg-red-600 active:scale-95"
            aria-label="End call"
          >
            <PhoneXMarkIcon className="size-6 text-white" />
          </button>
        </>
      ) : (
        <>
          {/* ⏱ Timer - only shows when connected */}
          <div className="text-sm tracking-widest text-neutral-500">
            {mm}
            :
            {ss}
          </div>

          {/* 🟣 Pulsating Bestie Avatar - only shows when connected */}
          <div className="flex flex-1 items-center justify-center">
            <PulsatingBestieAvatar isConnected={connectionState === 'connected'} />
          </div>

          {/* ⛔ End Button */}
          <button
            type="button"
            onClick={onClose}
            className="flex size-14 items-center justify-center rounded-full bg-red-500 shadow-lg transition hover:bg-red-600 active:scale-95"
            aria-label="End call"
          >
            <PhoneXMarkIcon className="size-6 text-white" />
          </button>
        </>
      )}

      {/* 🧠 Headless Conversation Engine (no UI) - only render when token is ready */}
      {conversationToken && !tokenError && (
        <ElevenLabsConversation
          conversationToken={conversationToken}
          locale={locale}
          onConnectionStateChange={setConnectionState}
          onIdentityLoading={setIsLoadingIdentity}
          onDisconnect={onClose}
        />
      )}
    </div>
  );
}
